import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import moment from 'moment';

import GridItem from '@/components/Grid/GridItem';
import GridContainer from '@/components/Grid/GridContainer';

import Card from '@/components/Card/Card';

import DatePicker from 'react-datepicker';
import GenericDoughnutChart from './GenericDoughnutChart';

import Icon from '@mui/material/Icon';
import styles from '@/assets/jss/material-dashboard-react/views/dashboardStyle';
import makeStyles from '@mui/styles/makeStyles';
import { API_BASE_URL } from '@/config';
import { CSVLink } from 'react-csv';
import DashboardInfoCard from './DashboardInfoCard';
import { useGetFacilities } from '@/api/hooks/facilities/useGetFacilities';

const postageCodes = {
  '1': 'USPS First Class',
  '2': 'USPS Priority',
  '3': 'USPS Priority + Delivery Confirmation',
  '5': 'FedEx Express 2 Day',
  '6': 'FedEx Express Overnight',
  '8': 'USPS Overnight',
  '9': 'USPS Certified Mail',
  '10': 'FedEx Priority Overnight',
};
const defaultPostageData = {
  insurance: {
    '1': 0,
    '2': 0,
    '3': 0,
    '5': 0,
    '6': 0,
    '8': 0,
    '9': 0,
    '10': 0,
  },
  regular: {
    '1': 0,
    '2': 0,
    '3': 0,
    '5': 0,
    '6': 0,
    '8': 0,
    '9': 0,
    '10': 0,
  },
  business: {
    '1': 0,
    '2': 0,
    '3': 0,
    '5': 0,
    '6': 0,
    '8': 0,
    '9': 0,
    '10': 0,
  },
};

const defaultEnvelopeData = {
  '#10 Envelope USPS First Class': 0,
  '6x9 Envelope USPS First Class': 0,
  '9x12 Flat Envelope USPS First Class': 0,
  'FedEx Express/USPS Priority Flat Rate Envelope': 0,
  'USPS Priority Medium Box': 0,
};
const useStyles = makeStyles(styles);

function PostageAccounting() {
  const { t } = useTranslation();
  const classes = useStyles();

  const [dates, setDates] = useState({
    startDate: moment().subtract(1, 'months').toDate(),
    endDate: moment().toDate(),
  });

  const { facilities } = useGetFacilities({
    allColumns: false,
  });
  const [facilityId, setFacilityId] = useState(null);
  const [pdfPageCount, setPdfPageCount] = useState({
    insurance: 0,
    regular: 0,
    business: 0,
  });
  const [checkCount, setCheckCount] = useState({
    insurance: 0,
    regular: 0,
    business: 0,
  });

  const [postageCounts, setPostageCounts] = useState(defaultPostageData);

  const [envelopeData, setEnvelopeData] = useState({
    insurance: defaultEnvelopeData,
    regular: defaultEnvelopeData,
    business: defaultEnvelopeData,
  });

  const [chartType, setChartType] = useState('insurance');
  const [envelopeChartType, setEnvelopeChartType] = useState('insurance');

  const [immediatePayments, setImmediatePayments] = useState({
    manualImmediatePayments: 0,
    fileImmediatePayments: 0,
  });

  const handleChartTypeChange = (e) => {
    setChartType(e.target.value);
  };

  const handleEnvelopeChartTypeChange = (e) => {
    setEnvelopeChartType(e.target.value);
  };

  const postageLabels = Object.values(postageCodes);
  const postageDataInsurance = Object.values(postageCounts.insurance);
  const postageDataRegular = Object.values(postageCounts.regular);
  const postageDataBusiness = Object.values(postageCounts.business);

  const chartData =
    chartType === 'insurance'
      ? postageDataInsurance
      : chartType === 'regular'
      ? postageDataRegular
      : postageDataBusiness;

  const fetchPostageData = async () => {
    try {
      const response = await axios.post(API_BASE_URL + '/postage_accounting', {
        startDate: dates.startDate,
        endDate: dates.endDate,
        facility_id: facilityId,
      });

      const {
        postageCodeData,
        totalPdfCount,
        totalCheckCount,
        envelopeData,
        manualImmediatePayments = 0,
        fileImmediatePayments = 0,
      } = response.data;

      setPostageCounts(() => ({
        insurance: {
          ...defaultPostageData.insurance,
          ...postageCodeData.insurance,
        },
        regular: { ...defaultPostageData.regular, ...postageCodeData.regular },
        business: {
          ...defaultPostageData.business,
          ...postageCodeData.business,
        },
      }));

      setEnvelopeData({
        insurance: {
          ...defaultEnvelopeData,
          ...envelopeData.insurance,
        },
        regular: {
          ...defaultEnvelopeData,
          ...envelopeData.regular,
        },
        business: {
          ...defaultEnvelopeData,
          ...envelopeData.business,
        },
      });

      setPdfPageCount(totalPdfCount);
      setCheckCount(totalCheckCount);

      setImmediatePayments({
        manualImmediatePayments,
        fileImmediatePayments,
      });
    } catch (error) {
      console.error('Error fetching postage data:', error);
    }
  };

  useEffect(() => {
    fetchPostageData();
  }, [dates, facilityId]);

  const postageBackgroundColors = [
    '#9966FF',
    '#8FAA66',
    '#FF6384',
    '#4BC0C0',
    '#36A2EB',
    '#FF9F40',
    '#FFCD56',
    '#C9CBCF',
    '#FF6384',
    '#36A2EB',
  ];

  const changeDate = (date) => {
    setDates((prevDates) => ({ ...prevDates, startDate: date }));
  };

  const changeEndDate = (date) => {
    setDates((prevDates) => ({ ...prevDates, endDate: date }));
  };

  const handleFacility = (e) => {
    setFacilityId(e.target.value);
  };

  const tooltipCallback = (tooltipItem) => {
    return `${tooltipItem.label}: ${tooltipItem.raw}`;
  };

  const csvPostageReport = {
    data: [
      ...Object.keys(postageCodes).map((key) => [
        postageCodes[key],
        postageCounts.insurance[key],
        postageCounts.regular[key],
        postageCounts.business[key],
      ]),
    ],
    headers: [
      t('postageAccounting.csv.postageTypeHeader'),
      t('postageAccounting.csv.insuranceCountHeader'),
      t('postageAccounting.csv.regularCountHeader'),
      t('postageAccounting.csv.businessCountHeader'),
    ],
    filename: 'Postage_Report.csv',
  };

  const csvEnvelopeReport = {
    data: [
      ...Object.keys(defaultEnvelopeData).map((key) => [
        key,
        envelopeData.insurance[key],
        envelopeData.regular[key],
        envelopeData.business[key],
      ]),
    ],
    headers: [
      t('postageAccounting.csv.envelopeTypeHeader'),
      t('postageAccounting.csv.insuranceCountHeader'),
      t('postageAccounting.csv.regularCountHeader'),
      t('postageAccounting.csv.businessCountHeader'),
    ],
    filename: 'Envelope_Report.csv',
  };

  const envelopeLabels = Object.keys(defaultEnvelopeData);
  const envelopeDataInsurance = Object.values(envelopeData.insurance);
  const envelopeDataRegular = Object.values(envelopeData.regular);
  const envelopeDataBusiness = Object.values(envelopeData.business);

  const envelopeChartData =
    envelopeChartType === 'insurance'
      ? envelopeDataInsurance
      : envelopeChartType === 'regular'
      ? envelopeDataRegular
      : envelopeDataBusiness;

  return (
    <div>
      {' '}
      <div className='dash_card'>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Card className='filterCard'>
              {/* <p className="filter_txt">Filter By:</p> */}
              <div>
                {' '}
                <div className='t-header search-facility-admin'>
                  <div className='t-header-left'>
                    {t('postageAccounting.dateFilters.fromLabel')}&nbsp;&nbsp;
                    <DatePicker
                      className='full-width-elem-date-picker'
                      id='dt'
                      selected={dates.startDate}
                      onChange={changeDate}
                    />
                  </div>
                  <div className='t-header-left'>
                    {t('postageAccounting.dateFilters.toLabel')}&nbsp;&nbsp;
                    <DatePicker
                      className='full-width-elem-date-picker'
                      id='dt'
                      selected={dates.endDate}
                      onChange={changeEndDate}
                    />
                  </div>
                  <div className='t-header-right'>
                    <div>
                      <select
                        id='status'
                        className='full-width-elem'
                        onChange={handleFacility}>
                        <option value={'null'}>
                          {t('postageAccounting.filters.facilityPlaceholder')}
                        </option>
                        {facilities &&
                          facilities.length > 0 &&
                          facilities.map((obj, index) => (
                            <option value={obj.id} key={index}>
                              {obj.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                  {/* <div className='search_t_head'>
                    <Button
                      type='button'
                      color='primary'
                      onClick={searchReport}>
                      <img src={search} className='search_ico' />
                    </Button>
                  </div> */}
                </div>
              </div>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
      <GridContainer>
        <GridItem xs={4} sm={4} md={4}>
          <DashboardInfoCard
            color='success'
            icon={<Icon>picture_as_pdf</Icon>}
            category={t('postageAccounting.cards.totalPageCount')}
            value={
              <span style={{ fontSize: '16px' }}>
                {t('postageAccounting.stats.insurance')}:{' '}
                {pdfPageCount.insurance + checkCount.insurance} |{' '}
                {t('postageAccounting.stats.regular')}:{' '}
                {pdfPageCount.regular + checkCount.regular} |{' '}
                {t('postageAccounting.stats.business')}:{' '}
                {pdfPageCount.business + checkCount.business}
              </span>
            }
            footer={
              <div className={classes.stats}>
                <b>
                  {t('postageAccounting.stats.attachmentsPageCount')}{' '}
                  <b>{t('postageAccounting.stats.insurance')} - </b>
                  <span>{pdfPageCount.insurance}</span> |{' '}
                  <b>{t('postageAccounting.stats.regular')} - </b>
                  <span>{pdfPageCount.regular}</span> |{' '}
                  <b>{t('postageAccounting.stats.business')} - </b>
                  <span>{pdfPageCount.business}</span>
                </b>
              </div>
            }
          />
        </GridItem>

        <GridItem xs={4} sm={4} md={4}>
          <DashboardInfoCard
            color='warning'
            icon={<Icon>payments</Icon>}
            category={t('postageAccounting.cards.immediatePayments')}
            value={
              <span style={{ fontSize: '16px' }}>
                {t('postageAccounting.stats.total')}{' '}
                {immediatePayments.manualImmediatePayments +
                  immediatePayments.fileImmediatePayments}
              </span>
            }
            footer={
              <div className={classes.stats}>
                <b>
                  {t('postageAccounting.stats.immediatePayment')}{' '}
                  <b>{t('postageAccounting.stats.manual')} - </b>
                  <span>
                    {immediatePayments.manualImmediatePayments}
                  </span> | <b>{t('postageAccounting.stats.file')} - </b>
                  <span>{immediatePayments.fileImmediatePayments}</span>
                </b>
              </div>
            }
          />
        </GridItem>

        <GridItem xs={4} sm={4} md={4}>
          <DashboardInfoCard
            color='info'
            icon={<Icon>outgoing_mail</Icon>}
            category={t('postageAccounting.cards.totalEnvelopeCount')}
            value={
              <span style={{ fontSize: '16px' }}>
                {t('postageAccounting.stats.insurance')}: {checkCount.insurance}{' '}
                | {t('postageAccounting.stats.regular')}: {checkCount.regular} |{' '}
                {t('postageAccounting.stats.business')}: {checkCount.business}
              </span>
            }
            footer={
              <div className={classes.stats}>
                <b>{t('postageAccounting.cards.totalEnvelopeCount')}</b>
              </div>
            }
          />
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12} sm={6}>
          <Card className='dashboardCard'>
            <h3 className='chart_heads'>
              {t('postageAccounting.charts.envelopeData')}
            </h3>
            <div style={{ width: '100%', height: '80%', paddingTop: '10%' }}>
              <GenericDoughnutChart
                labels={envelopeLabels}
                data={envelopeChartData}
                backgroundColors={postageBackgroundColors}
                tooltipCallback={tooltipCallback}
              />
            </div>
            <div className='dashboardRadio'>
              <div style={{ fontSize: '12px' }}>
                <input
                  type='radio'
                  value='insurance'
                  onChange={handleEnvelopeChartTypeChange}
                  checked={envelopeChartType === 'insurance'}
                  name='envelopeChartType'
                />{' '}
                {t('postageAccounting.radio.envelopeDataInsurance')}
              </div>{' '}
              <div style={{ fontSize: '12px' }}>
                <input
                  type='radio'
                  value='regular'
                  onChange={handleEnvelopeChartTypeChange}
                  checked={envelopeChartType === 'regular'}
                  name='envelopeChartType'
                />{' '}
                {t('postageAccounting.radio.envelopeDataRegular')}
              </div>
              <div style={{ fontSize: '12px' }}>
                <input
                  type='radio'
                  value='business'
                  onChange={handleEnvelopeChartTypeChange}
                  checked={envelopeChartType === 'business'}
                  name='envelopeChartType'
                />{' '}
                {t('postageAccounting.radio.envelopeDataBusiness')}
              </div>
            </div>
            <CSVLink
              {...csvEnvelopeReport}
              style={{ cursor: 'pointer' }}
              className='dashboardBtn'>
              {t('postageAccounting.charts.downloadReport')}
            </CSVLink>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={6}>
          <Card className='dashboardCard'>
            <h3 className='chart_heads'>
              {t('postageAccounting.charts.envelopeData')}
            </h3>
            {Object.keys(defaultEnvelopeData).map((key, index) => (
              <div key={index}>
                <b>
                  <p>{key}</p>
                </b>
                <p>
                  {t('postageAccounting.stats.insurance')}:{' '}
                  {envelopeData.insurance[key]} |{' '}
                  {t('postageAccounting.stats.regular')}:{' '}
                  {envelopeData.regular[key]} |{' '}
                  {t('postageAccounting.stats.business')}:{' '}
                  {envelopeData.business[key]}
                </p>
              </div>
            ))}
          </Card>
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12} sm={6}>
          <Card className='dashboardCard'>
            <h3 className='chart_heads'>
              {t('postageAccounting.charts.postageCarriers')}
            </h3>
            <div style={{ width: '100%', height: '80%', paddingTop: '10%' }}>
              <GenericDoughnutChart
                labels={postageLabels}
                data={chartData}
                backgroundColors={postageBackgroundColors}
                tooltipCallback={tooltipCallback}
              />
            </div>
            <div className='dashboardRadio'>
              <div style={{ fontSize: '12px' }}>
                <input
                  type='radio'
                  value='insurance'
                  onChange={handleChartTypeChange}
                  checked={chartType === 'insurance'}
                  name='chartType'
                />{' '}
                {t('postageAccounting.radio.postageDataInsurance')}
              </div>{' '}
              <div style={{ fontSize: '12px' }}>
                <input
                  type='radio'
                  value='regular'
                  onChange={handleChartTypeChange}
                  checked={chartType === 'regular'}
                  name='chartType'
                />{' '}
                {t('postageAccounting.radio.postageDataRegular')}
              </div>
              <div style={{ fontSize: '12px' }}>
                <input
                  type='radio'
                  value='business'
                  onChange={handleChartTypeChange}
                  checked={chartType === 'business'}
                  name='chartType'
                />{' '}
                {t('postageAccounting.radio.postageDataBusiness')}
              </div>
            </div>
            <CSVLink
              {...csvPostageReport}
              style={{ cursor: 'pointer' }}
              className='dashboardBtn'>
              {t('postageAccounting.charts.downloadReport')}
            </CSVLink>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={6}>
          <Card className='dashboardCard'>
            <h3 className='chart_heads'>
              {t('postageAccounting.charts.postageCarriers')}
            </h3>
            {Object.keys(postageCodes).map((key, index) => (
              <div key={index}>
                <b>
                  <p>{postageCodes[key]}</p>
                </b>
                <p>
                  {t('postageAccounting.stats.insurance')}:{' '}
                  {postageCounts.insurance[key]} |{' '}
                  {t('postageAccounting.stats.regular')}:{' '}
                  {postageCounts.regular[key]} |{' '}
                  {t('postageAccounting.stats.business')}:{' '}
                  {postageCounts.business[key]}
                </p>
              </div>
            ))}
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}

export default PostageAccounting;
